

import { 
  collection, 
  addDoc, 
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

const trainingExemplarsCollection = collection(db, 'trainingExemplars');

const handleFirestoreError = (error: any, context: string) => {
    console.error(`Firestore error during ${context}:`, error);
    let userMessage = `An error occurred with the database while ${context}.`;

    if (error && error.code) {
        switch (error.code) {
            case 'permission-denied':
                userMessage = 'Database permission denied. Please check your Firestore security rules in the Firebase console.';
                break;
            case 'unauthenticated':
                userMessage = 'You are not authenticated. Please log in again.';
                break;
            case 'failed-precondition':
                userMessage = 'A database precondition failed. This can happen if a required index has not been created. Check the developer console for a link to create the necessary index in Firebase.';
                break;
            case 'not-found':
                userMessage = 'The database was not found. Please ensure you have created a Firestore database in "Native Mode" in your Firebase project console.';
                break;
            case 'unavailable':
                 userMessage = 'The database service is currently unavailable. Please try again later.';
                 break;
        }
    } else if (error && error.message && (error.message.includes('400') || error.message.includes('Bad Request'))) {
         userMessage = 'Received a "Bad Request" from the database server. This commonly means the Firestore database has not been created yet. Please go to the Firestore section of your Firebase console and click "Create Database".';
    }

    throw new Error(userMessage);
};


/**
 * Saves a new training exemplar to the Firestore database.
 * 
 * In a production RAG system, this function would not write directly to Firestore.
 * Instead, it would call a secure backend service (e.g., a Cloud Function).
 * That backend service would:
 * 1. Receive the text data.
 * 2. Call a text embedding model (like Vertex AI's text-embedding-004) to vectorize the essay and rubric.
 * 3. Store the resulting vectors along with the metadata (criteriaBands, overallScore, yearLevel, etc.) in a vector database (like Vertex AI Vector Search).
 * 
 * For this demonstration, we are storing the raw text and metadata in Firestore, which can serve as a trigger for such a backend process.
 * 
 * @param {object} exemplarData - The data for the training exemplar.
 * @param {string} exemplarData.essayText - The text of the example essay.
 * @param {string} exemplarData.rubric - The rubric used to mark the essay.
 * @param {string} exemplarData.yearLevel - The year level of the student.
 * @param {Array<{name: string, band: string}>} exemplarData.criteriaBands - An array of criterion names and their achieved bands.
 * @param {string} exemplarData.overallScore - The overall score for the essay.
 * @param {string} [exemplarData.explanation] - An optional explanation for the score.
 * @param {string} userId - The ID of the admin user uploading the exemplar.
 * @returns {object} The newly created document data, including its ID.
 */
interface ExemplarData {
  essayText: string;
  rubric: string;
  yearLevel: string;
  criteriaBands: Array<{name: string; band: string}>;
  overallScore: number | string;
  explanation?: string;
}

export const addTrainingExemplar = async (exemplarData: ExemplarData, userId: string) => {
  if (!userId) {
    throw new Error("Cannot add training exemplar without an admin user ID.");
  }

  // Updated validation logic for the new data structure
  const { essayText, rubric, yearLevel, criteriaBands, overallScore, explanation } = exemplarData;

  if (!essayText || !rubric || !yearLevel || !overallScore) {
    throw new Error("Essay, Rubric, Year Level, and Overall Score fields must be provided.");
  }
  if (!Array.isArray(criteriaBands) || criteriaBands.length === 0) {
    throw new Error("At least one criterion band must be provided.");
  }
  const areCriteriaValid = criteriaBands.every(c => c && typeof c.name === 'string' && c.name.trim() !== '' && typeof c.band === 'string' && c.band.trim() !== '');
  if (!areCriteriaValid) {
    throw new Error("Each criterion must have a non-empty name and band.");
  }

  try {
    const newDoc = {
      essayText,
      rubric,
      yearLevel,
      criteriaBands, // This is the array of objects
      overallScore,
      explanation: explanation || "", // Ensure it's at least an empty string
      userId: userId,
      createdAt: serverTimestamp(),
    };
    const docRef = await addDoc(trainingExemplarsCollection, newDoc);
    return { id: docRef.id, ...newDoc };
  } catch (error) {
    handleFirestoreError(error, 'saving the training exemplar');
  }
};
